import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import SearchOverlay from '@/components/feature/SearchOverlay';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/about', label: '关于我们' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b transition-colors duration-300 ${
        scrolled
          ? 'border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-transparent bg-background-50/70 backdrop-blur'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4 md:h-20">
          <Link to="/" className="flex items-center gap-3 whitespace-nowrap" aria-label="猎奇吃瓜首页">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50 dark:text-foreground-950">
              <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl text-foreground-950 md:text-2xl">猎奇吃瓜</span>
              <span className="hidden text-[11px] tracking-wide text-foreground-500 md:block">
                奇葩人物 · 迷惑行为 · 沙雕新闻
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            <Link
              to="/"
              className="rounded-md px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              首页
            </Link>
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                aria-expanded={catOpen}
              >
                主题分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={cat.icon} aria-hidden="true"></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="font-medium">{cat.name}</span>
                        <span className="text-xs text-foreground-500">{cat.count} 篇内容</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700 md:px-4"
              aria-label="搜索内容"
            >
              <i className="ri-search-line" aria-hidden="true"></i>
              <span className="hidden md:inline">搜索想看的瓜</span>
            </button>
            <Link
              to="/about"
              className="hidden items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex dark:text-foreground-950"
            >
              <i className="ri-send-plane-line" aria-hidden="true"></i>
              我要爆料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-xl text-foreground-800 lg:hidden"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>
            <p className="px-3 pt-2 text-xs text-foreground-500">主题分类</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50"
                >
                  <i className={`${cat.icon} text-primary-600`} aria-hidden="true"></i>
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-2 rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
            <Link
              to="/about"
              className="mt-1 flex items-center justify-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 dark:text-foreground-950"
            >
              <i className="ri-send-plane-line" aria-hidden="true"></i>
              我要爆料
            </Link>
          </div>
        </div>
      )}

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </header>
  );
}